import { useMemo, useState } from "react";
import HumanizationComparisonPanel from "./HumanizationComparisonPanel";
import HumanizationPanel from "./HumanizationPanel";
import type {
  StudioImproveLoopReadModel,
  StudioImproveReviewState,
  StudioImproveSuggestion,
} from "../../../lib/studio/studioImproveReadModel";

function toneForState(state: StudioImproveReviewState | "review" | "pass" | "warn" | "fail") {
  if (state === "applied" || state === "pass") {
    return {
      background: "rgba(44, 93, 67, 0.14)",
      color: "#214735",
    };
  }

  if (state === "blocked" || state === "fail") {
    return {
      background: "rgba(162, 62, 62, 0.12)",
      color: "#812d2d",
    };
  }

  if (state === "rejected" || state === "warn") {
    return {
      background: "rgba(181, 141, 34, 0.16)",
      color: "#6d5210",
    };
  }

  return {
    background: "rgba(41, 84, 103, 0.1)",
    color: "#295467",
  };
}

function pill(label: string, state: StudioImproveReviewState | "review" | "pass" | "warn" | "fail") {
  const tone = toneForState(state);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "0.28rem 0.62rem",
        background: tone.background,
        color: tone.color,
        fontSize: "0.74rem",
        fontWeight: 700,
        textTransform: "capitalize",
      }}
    >
      {label}
    </span>
  );
}

function suggestionStateLabel(state: StudioImproveReviewState) {
  if (state === "applied") return "Applied to draft";
  if (state === "rejected") return "Rejected";
  if (state === "blocked") return "Blocked";
  return "Pending review";
}

function summaryCard(label: string, value: string, detail: string) {
  return (
    <div
      style={{
        borderRadius: 20,
        border: "1px solid rgba(54, 44, 35, 0.08)",
        background:
          "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
        padding: "0.95rem",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#85796e",
          fontWeight: 700,
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "0.38rem",
          color: "#26221d",
          fontWeight: 700,
          fontSize: "1.05rem",
        }}
      >
        {value}
      </div>
      <p style={{ margin: "0.42rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
        {detail}
      </p>
    </div>
  );
}

export default function StudioImprovePanel(props: {
  model: StudioImproveLoopReadModel;
}) {
  const [reviewStateById, setReviewStateById] = useState<
    Record<string, StudioImproveReviewState>
  >(() =>
    Object.fromEntries(
      props.model.suggestedEdits.map((suggestion) => [
        suggestion.id,
        suggestion.reviewState,
      ])
    )
  );

  const suggestions = useMemo(
    () =>
      props.model.suggestedEdits.map((suggestion) => ({
        ...suggestion,
        reviewState: reviewStateById[suggestion.id] || suggestion.reviewState,
      })),
    [props.model.suggestedEdits, reviewStateById]
  );

  const reviewableSuggestions = suggestions.filter(
    (suggestion) => suggestion.reviewState !== "blocked"
  );
  const blockedSuggestions = suggestions.filter(
    (suggestion) => suggestion.reviewState === "blocked"
  );
  const appliedCount = suggestions.filter(
    (suggestion) => suggestion.reviewState === "applied"
  ).length;
  const rejectedCount = suggestions.filter(
    (suggestion) => suggestion.reviewState === "rejected"
  ).length;
  const pendingCount = suggestions.filter(
    (suggestion) => suggestion.reviewState === "pending"
  ).length;

  function updateReviewState(id: string, reviewState: StudioImproveReviewState) {
    setReviewStateById((current) => ({
      ...current,
      [id]: reviewState,
    }));
  }

  function renderSuggestionCard(suggestion: StudioImproveSuggestion & {
    reviewState: StudioImproveReviewState;
  }) {
    const tone = toneForState(suggestion.reviewState);

    return (
      <section
        key={suggestion.id}
        aria-label={`Suggestion ${suggestion.title}`}
        style={{
          borderRadius: 22,
          border: "1px solid rgba(54, 44, 35, 0.08)",
          background:
            "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
          padding: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "0.8rem",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "0.72rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#85796e",
                fontWeight: 700,
              }}
            >
              {suggestion.area}
            </div>
            <h4 style={{ margin: "0.4rem 0 0", fontSize: "1.02rem", color: "#26221d" }}>
              {suggestion.title}
            </h4>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: 999,
              padding: "0.28rem 0.62rem",
              background: tone.background,
              color: tone.color,
              fontSize: "0.74rem",
              fontWeight: 700,
            }}
          >
            {suggestionStateLabel(suggestion.reviewState)}
          </span>
        </div>

        <p style={{ margin: "0.6rem 0 0", color: "#4d453d", lineHeight: 1.65 }}>
          {suggestion.summary}
        </p>
        <div
          style={{
            marginTop: "0.8rem",
            borderRadius: 18,
            background: "#f8f4ee",
            border: "1px solid rgba(54, 44, 35, 0.08)",
            padding: "0.85rem",
            color: "#26221d",
            lineHeight: 1.6,
          }}
        >
          {suggestion.draftChange}
        </div>
        <p style={{ margin: "0.72rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
          {suggestion.rationale}
        </p>
        <div
          style={{
            marginTop: "0.75rem",
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
          }}
        >
          {suggestion.sourceLabels.map((label) => (
            <span
              key={`${suggestion.id}_${label}`}
              style={{
                borderRadius: 999,
                padding: "0.25rem 0.55rem",
                background: "rgba(41, 84, 103, 0.08)",
                color: "#295467",
                fontSize: "0.76rem",
                fontWeight: 700,
              }}
            >
              {label}
            </span>
          ))}
          {suggestion.blockedReason ? (
            <span
              style={{
                borderRadius: 999,
                padding: "0.25rem 0.55rem",
                background: "rgba(162, 62, 62, 0.12)",
                color: "#812d2d",
                fontSize: "0.76rem",
                fontWeight: 700,
              }}
            >
              {suggestion.blockedReason}
            </span>
          ) : null}
        </div>

        {suggestion.reviewState !== "blocked" ? (
          <div
            style={{
              marginTop: "0.95rem",
              display: "flex",
              gap: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              aria-label={`Apply ${suggestion.title}`}
              onClick={() => updateReviewState(suggestion.id, "applied")}
              style={{
                borderRadius: 999,
                padding: "0.74rem 1rem",
                border: "1px solid rgba(41, 84, 103, 0.18)",
                background: "linear-gradient(135deg, #173847, #285466)",
                color: "#f7f4ee",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Apply to draft
            </button>
            <button
              type="button"
              aria-label={`Reject ${suggestion.title}`}
              onClick={() => updateReviewState(suggestion.id, "rejected")}
              style={{
                borderRadius: 999,
                padding: "0.74rem 1rem",
                border: "1px solid rgba(54, 44, 35, 0.12)",
                background: "#fff9f2",
                color: "#3e372f",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Reject
            </button>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section
      aria-labelledby="studio-improve-heading"
      style={{
        marginTop: "1.25rem",
        borderRadius: 30,
        border: "1px solid rgba(54, 44, 35, 0.1)",
        background: "rgba(255, 252, 248, 0.96)",
        padding: "1.2rem",
        boxShadow: "0 24px 50px rgba(77, 61, 44, 0.07)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ maxWidth: 760 }}>
          <div
            style={{
              fontSize: "0.78rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#7b726a",
              fontWeight: 700,
            }}
          >
            A22.4 improve loop
          </div>
          <h3
            id="studio-improve-heading"
            style={{
              margin: "0.45rem 0 0",
              fontSize: "clamp(1.5rem, 2.5vw, 2rem)",
              lineHeight: 1.1,
              fontFamily: "Georgia, 'Times New Roman', serif",
            }}
          >
            {props.model.title}
          </h3>
          <p style={{ margin: "0.75rem 0 0", color: "#4d453d", lineHeight: 1.7 }}>
            {props.model.summary}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          {pill(`${appliedCount} applied`, appliedCount > 0 ? "applied" : "pending")}
          {pill(`${rejectedCount} rejected`, rejectedCount > 0 ? "rejected" : "pending")}
          {pill(`${blockedSuggestions.length} blocked`, blockedSuggestions.length > 0 ? "blocked" : "pending")}
        </div>
      </div>

      <div
        style={{
          marginTop: "1rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: "0.85rem",
        }}
      >
        {summaryCard(
          "Recent interactions",
          String(props.model.recentInteractions.length),
          "Previews, calls, and conversations stay review-only inside Studio."
        )}
        {summaryCard(
          "Flagged friction",
          String(props.model.flaggedFriction.length),
          "Current friction comes from deterministic QA and bounded review rules."
        )}
        {summaryCard(
          "Draft queue",
          `${pendingCount} pending`,
          "Apply and reject only change local draft review state."
        )}
      </div>

      <HumanizationPanel
        snapshot={props.model.humanization.currentPreview}
        recommendations={props.model.humanization.tuningRecommendations}
      />

      <HumanizationComparisonPanel comparison={props.model.humanization.comparison} />

      <div
        style={{
          marginTop: "1.1rem",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 0.95fr)",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <section>
          <div
            style={{
              fontSize: "0.74rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#7b726a",
              fontWeight: 700,
            }}
          >
            Recent interactions
          </div>
          <div style={{ marginTop: "0.75rem", display: "grid", gap: "0.85rem" }}>
            {props.model.recentInteractions.map((interaction) => (
              <section
                key={interaction.id}
                style={{
                  borderRadius: 22,
                  border: "1px solid rgba(54, 44, 35, 0.08)",
                  background:
                    "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
                  padding: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "0.8rem",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#85796e",
                        fontWeight: 700,
                      }}
                    >
                      {interaction.sourceLabel}
                    </div>
                    <div style={{ marginTop: "0.34rem", fontWeight: 700, color: "#26221d" }}>
                      {interaction.channel} / {interaction.stage}
                    </div>
                  </div>
                  {pill(interaction.qaLabel, interaction.qaStatus)}
                </div>
                <div
                  style={{
                    marginTop: "0.75rem",
                    display: "grid",
                    gap: "0.7rem",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#85796e",
                        fontWeight: 700,
                      }}
                    >
                      Customer
                    </div>
                    <p style={{ margin: "0.34rem 0 0", color: "#4d453d", lineHeight: 1.6 }}>
                      {interaction.customerExcerpt}
                    </p>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#85796e",
                        fontWeight: 700,
                      }}
                    >
                      Assistant
                    </div>
                    <p style={{ margin: "0.34rem 0 0", color: "#4d453d", lineHeight: 1.6 }}>
                      {interaction.assistantExcerpt}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: "0.75rem",
                    display: "flex",
                    gap: "0.45rem",
                    flexWrap: "wrap",
                  }}
                >
                  {interaction.flaggedFriction.length > 0 ? (
                    interaction.flaggedFriction.map((label) => (
                      <span
                        key={`${interaction.id}_${label}`}
                        style={{
                          borderRadius: 999,
                          padding: "0.25rem 0.55rem",
                          background: "rgba(181, 141, 34, 0.16)",
                          color: "#6d5210",
                          fontSize: "0.76rem",
                          fontWeight: 700,
                        }}
                      >
                        {label}
                      </span>
                    ))
                  ) : (
                    <span
                      style={{
                        borderRadius: 999,
                        padding: "0.25rem 0.55rem",
                        background: "rgba(44, 93, 67, 0.14)",
                        color: "#214735",
                        fontSize: "0.76rem",
                        fontWeight: 700,
                      }}
                    >
                      no friction
                    </span>
                  )}
                </div>
              </section>
            ))}
          </div>
        </section>

        <section>
          <div
            style={{
              fontSize: "0.74rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#7b726a",
              fontWeight: 700,
            }}
          >
            Flagged friction
          </div>
          <div style={{ marginTop: "0.75rem", display: "grid", gap: "0.8rem" }}>
            {props.model.flaggedFriction.map((friction) => (
              <section
                key={friction.id}
                style={{
                  borderRadius: 22,
                  border: "1px solid rgba(54, 44, 35, 0.08)",
                  background:
                    "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
                  padding: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "0.75rem",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#26221d" }}>{friction.label}</div>
                  {pill(friction.severity, friction.severity)}
                </div>
                <p style={{ margin: "0.5rem 0 0", color: "#4d453d", lineHeight: 1.6 }}>
                  {friction.detail}
                </p>
                <div style={{ marginTop: "0.55rem", color: "#7b726a", fontSize: "0.85rem" }}>
                  {friction.interactionLabel}
                </div>
              </section>
            ))}
          </div>
        </section>
      </div>

      <section style={{ marginTop: "1.15rem" }}>
        <div
          style={{
            fontSize: "0.74rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#7b726a",
            fontWeight: 700,
          }}
        >
          Suggested edits
        </div>
        <div style={{ marginTop: "0.75rem", display: "grid", gap: "0.85rem" }}>
          {reviewableSuggestions.map((suggestion) => renderSuggestionCard(suggestion))}
        </div>
      </section>

      <section style={{ marginTop: "1.15rem" }}>
        <div
          style={{
            fontSize: "0.74rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#7b726a",
            fontWeight: 700,
          }}
        >
          Blocked suggestions
        </div>
        <div style={{ marginTop: "0.75rem", display: "grid", gap: "0.85rem" }}>
          {blockedSuggestions.map((suggestion) => renderSuggestionCard(suggestion))}
        </div>
      </section>

      <div
        style={{
          marginTop: "1.15rem",
          borderRadius: 22,
          border: "1px solid rgba(41, 84, 103, 0.14)",
          background:
            "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
          padding: "1rem",
        }}
      >
        <div
          style={{
            fontSize: "0.74rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#5b7884",
            fontWeight: 700,
          }}
        >
          Safe iteration posture
        </div>
        <ul style={{ margin: "0.65rem 0 0", paddingLeft: "1.15rem", color: "#39535f", lineHeight: 1.7 }}>
          {props.model.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}
